import type React from 'react';
import { useState } from 'react';
import Modal from '@common/components/ConstellationV10Modal';
import userPersonas from '@common/constants/userPersonas';
import FaqConstants from '@rental-manager/constants/ConstellationFaqConstants';
import { isDueDateValid } from './validation';
import dueDates from './dueDates.json';
import {
  Box,
  Button,
  ButtonGroup,
  FormHelp,
  Heading,
  Label,
  Paragraph,
  Select,
  TextButton,
} from '@zillow/constellation-10';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

interface Props {
  paymentDayOfTheMonth: number;
  editMode: boolean;
  onSubmit: (requestPayload: Record<string, unknown>) => any;
  onClose: () => void;
  onCancel: () => void;
  onBack: (requestPayload: Record<string, unknown>) => void;
  trackValidationError: (data: { errors: Array<Record<string, any>> }) => void;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}

const DueDatePage: React.FC<Props> = ({
  paymentDayOfTheMonth: initialPaymentDayOfTheMonth = 1,
  editMode = false,
  onSubmit,
  onClose,
  onCancel,
  onBack,
  trackValidationError,
  currentProperty,
  user,
}) => {
  const [paymentDayOfTheMonth, setPaymentDayOfTheMonth] = useState(initialPaymentDayOfTheMonth);
  const [showCaveat, setShowCaveat] = useState(false);
  const [error, setError] = useState('');
  const [editPaymentDayOfTheMonth, setEditPaymentDayOfTheMonth] = useState(false);

  const onChangeSelect = (e): void => {
    const selectedOption = e.currentTarget.options[e.currentTarget.selectedIndex];
    const newPaymentDayOfTheMonth = parseInt(selectedOption.getAttribute('value'), 10);
    const newShowCaveat = selectedOption.innerText?.endsWith('*');

    setShowCaveat(newShowCaveat);
    setPaymentDayOfTheMonth(newPaymentDayOfTheMonth);
    setError('');
    setEditPaymentDayOfTheMonth(true);
  };

  const handleSubmit = (): Promise<any> => {
    return Promise.resolve().then(() => {
      const validationError = isDueDateValid(paymentDayOfTheMonth);

      if (validationError) {
        setError(validationError);
        trackValidationError({ errors: [{ dueDateError: validationError }] });
        return { error: true };
      }

      IreneOnboardingCSEvents.emitContinueFromDueDatePageEvent({ currentProperty, user });

      const payload = editMode
        ? {
            editDayOfTheMonth: paymentDayOfTheMonth,
          }
        : {
            paymentDayOfTheMonth,
          };

      return onSubmit(payload);
    });
  };

  const handleCancel = (): void => {
    onCancel();
  };

  const handleClose = (): void => {
    IreneOnboardingCSEvents.emitExitFromOnboardingFlowEvent({
      currentProperty,
      user,
      triggerObject: 'due_day',
    });
    onClose();
  };

  const gotoPreviousPage = (): void => {
    IreneOnboardingCSEvents.emitBackFromOnboardingFlowEvent({
      currentProperty,
      user,
      triggerObject: 'due_day',
    });
    onBack({
      paymentDayOfTheMonth,
      editPaymentDayOfTheMonth,
    });
  };

  const renderStartDateOptions = () => {
    const defaultOption = (
      <option key={-1} disabled value="">
        Select one
      </option>
    );
    const options = dueDates.map((date, index) => {
      if (date === null) {
        return (
          <option key={index} disabled>
            ──────
          </option>
        );
      }

      const [text, value] = date;

      return (
        <option key={index} value={value}>
          {text}
        </option>
      );
    });

    return [defaultOption, ...options];
  };

  const startDateOptions = renderStartDateOptions();

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      onRequestClose={handleClose}
      header={
        <Box css={{ padding: 'token(spacing.sm) !important' }}>
          <TextButton textStyle="body-sm-bold" tone="brand" css={{ float: 'left' }} onClick={gotoPreviousPage}>
            Back
          </TextButton>
          <Heading level={3} textStyle="heading-xs" css={{ display: 'flex', justifyContent: 'center' }}>
            Payment setup
          </Heading>
        </Box>
      }
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'token(spacing.sm) !important',
            padding: 'token(spacing.lg) !important',
          }}
        >
          <Paragraph textStyle="body-lg-bold">When should your tenant send their rent each month?</Paragraph>
          <Box>
            <Label>Monthly payment date</Label>
            <Select size="md" value={paymentDayOfTheMonth} onChange={onChangeSelect}>
              {startDateOptions}
            </Select>
            {error && <FormHelp error>{error}</FormHelp>}
          </Box>

          {showCaveat && (
            <Paragraph textStyle="body-sm">
              For any month with fewer than {paymentDayOfTheMonth} days, the last day of the month will be the default
              payment date.
            </Paragraph>
          )}

          <Paragraph textStyle="body-sm">
            We&apos;ll ask your renter to send their rent&nbsp;
            <strong>on or before this day.</strong>
          </Paragraph>
          <Paragraph textStyle="body-sm">
            Once they send payment, it will be deposited&nbsp;
            <strong>up to 5 business days</strong> later, depending on the banks&apos; processing times.
          </Paragraph>
          <FaqComponent faqs={FaqConstants.ONBOARDING_V2.DUE_DATE_PAGE} />
        </Box>
      }
      footer={
        <ButtonGroup
          css={{
            padding: 'token(spacing.sm) !important',
          }}
          aria-label="Footer"
        >
          <Button emphasis="filled" onClick={handleSubmit}>
            {editMode ? 'Update' : 'Continue'}
          </Button>
          {editMode && (
            <Button emphasis="outlined" onClick={handleCancel}>
              Cancel
            </Button>
          )}
        </ButtonGroup>
      }
    />
  );
};

export default DueDatePage;
